'use client';

import { environment } from '@/config/environment';
import type {
  IDemoSessionResponse,
  ILineDemoHistoryEntry,
  TLineDemoMessage,
} from '@/interfaces/lineDemo';
import { demoBrowserContext, getDemoFingerprint } from '@/lib/demo-fingerprint';
import { demoLocationIdForBusinessType } from '@/lib/demo-business-types';
import { createDemoSessionCache } from '@/lib/demo-session-cache';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { getWebsiteSessionUuid } from '@/session-replay';

const ACCESS_TOKEN_REFRESH_BUFFER_MS = 10 * 60 * 1000;
const ACCESS_TOKEN_REFRESH_RETRY_MS = 30 * 1000;

interface IDemoChatResponse {
  demo_live_session_token: string;
  agent_session_uuid: string;
  messages: TLineDemoMessage[];
  needs_login: boolean;
}

interface IDemoSessionContext {
  accessToken: string | null;
  agentSessionUuid: string | null;
  lineHistory: ILineDemoHistoryEntry[] | null;
  customerName: string | null;
  dataRevision: number;
  locationId: number;
  setLocationId: (locationId: number) => void;
  ensureSession: (locationId?: number) => Promise<IDemoSessionResponse>;
  refreshSession: (locationId?: number) => Promise<IDemoSessionResponse>;
  sendLineMessage: (
    message: string,
    images?: { media_type: string; data: string }[]
  ) => Promise<TLineDemoMessage[]>;
}

const DemoSessionContext = createContext<IDemoSessionContext | null>(null);

export function DemoSessionProvider({
  language,
  initialLocationId = demoLocationIdForBusinessType('hairdressers'),
  children,
}: {
  language: 'en' | 'zh';
  initialLocationId?: number;
  children: ReactNode;
}) {
  const [locationId, setSelectedLocationId] = useState(initialLocationId);
  const locationRef = useRef(locationId);
  const [cache] = useState(() =>
    createDemoSessionCache({
      getItem: key => localStorage.getItem(key),
      setItem: (key, value) => localStorage.setItem(key, value),
      removeItem: key => localStorage.removeItem(key),
    })
  );
  const [activeSession, setActiveSession] =
    useState<IDemoSessionResponse | null>(null);
  const [dataRevision, setDataRevision] = useState(0);
  const accessToken = activeSession?.access_token ?? null;
  const accessTokenExpiresAt = activeSession?.access_token_expires_at ?? null;
  const agentSessionUuid = activeSession?.agent_session_uuid ?? null;
  const lineHistory = activeSession?.history ?? null;
  const customerName = activeSession
    ? [activeSession.customer_first_name, activeSession.customer_last_name]
        .filter(Boolean)
        .join(' ')
    : null;

  const setLocationId = useCallback(
    (nextLocationId: number) => {
      locationRef.current = nextLocationId;
      setSelectedLocationId(nextLocationId);
      setActiveSession(cache.get(nextLocationId));
    },
    [cache]
  );

  const request = useCallback(async (path: string, body: object) => {
    const fingerprint = getDemoFingerprint();
    const response = await fetch(
      `${environment.api.agentApiUrl.replace(/\/$/, '')}/agent/public/demo/${path}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-OB-Context': demoBrowserContext(fingerprint),
        },
        body: JSON.stringify(body),
      }
    );
    const payload = (await response.json().catch(() => ({}))) as {
      message?: string;
    };
    if (!response.ok) {
      const error = new Error(payload.message || `HTTP ${response.status}`);
      Object.assign(error, { status: response.status });
      throw error;
    }
    return payload;
  }, []);

  const requestSession = useCallback(
    async (
      forceRefresh = false,
      requestedLocationId = locationRef.current
    ): Promise<IDemoSessionResponse> => {
      const session = await cache.request(
        requestedLocationId,
        forceRefresh,
        token =>
          request('session', {
            interaction: true,
            language: language === 'zh' ? 'cn' : 'en',
            location_id: requestedLocationId,
            ...(token ? { demo_live_session_token: token } : {}),
          }) as unknown as Promise<IDemoSessionResponse>
      );
      // A response from the previous tab must never replace the selected customer.
      if (locationRef.current === requestedLocationId)
        setActiveSession(session);
      return session;
    },
    [cache, language, request]
  );

  const ensureSession = useCallback(
    (requestedLocationId?: number) =>
      requestSession(false, requestedLocationId),
    [requestSession]
  );
  const refreshSession = useCallback(
    (requestedLocationId?: number) => requestSession(true, requestedLocationId),
    [requestSession]
  );

  useEffect(() => {
    if (!cache.hasStarted() && !cache.storedToken(locationId)) return;
    void ensureSession(locationId).catch(() => undefined);
  }, [cache, ensureSession, locationId]);

  useEffect(() => {
    if (!accessTokenExpiresAt) return;

    let cancelled = false;
    let timeout: number | undefined;
    const refresh = async () => {
      try {
        await refreshSession();
      } catch {
        if (!cancelled) {
          timeout = window.setTimeout(refresh, ACCESS_TOKEN_REFRESH_RETRY_MS);
        }
      }
    };
    timeout = window.setTimeout(
      refresh,
      Math.max(
        1000,
        accessTokenExpiresAt * 1000 -
          Date.now() -
          ACCESS_TOKEN_REFRESH_BUFFER_MS
      )
    );
    return () => {
      cancelled = true;
      if (timeout !== undefined) window.clearTimeout(timeout);
    };
  }, [accessTokenExpiresAt, refreshSession]);

  useEffect(() => {
    if (!accessTokenExpiresAt) return;
    const refreshIfNeeded = () => {
      if (
        document.visibilityState === 'visible' &&
        accessTokenExpiresAt * 1000 - Date.now() <=
          ACCESS_TOKEN_REFRESH_BUFFER_MS
      ) {
        void refreshSession().catch(() => undefined);
      }
    };
    window.addEventListener('focus', refreshIfNeeded);
    document.addEventListener('visibilitychange', refreshIfNeeded);
    return () => {
      window.removeEventListener('focus', refreshIfNeeded);
      document.removeEventListener('visibilitychange', refreshIfNeeded);
    };
  }, [accessTokenExpiresAt, refreshSession]);

  const sendLineMessage = useCallback(
    async (
      message: string,
      images?: { media_type: string; data: string }[]
    ): Promise<TLineDemoMessage[]> => {
      const requestedLocationId = locationRef.current;
      let session = await ensureSession(requestedLocationId);
      const send = () =>
        request('chat', {
          demo_live_session_token: session.demo_live_session_token,
          language: language === 'zh' ? 'cn' : 'en',
          location_id: requestedLocationId,
          message,
          ...(images?.length ? { images } : {}),
          website_session_uuid: getWebsiteSessionUuid() ?? undefined,
        }) as Promise<unknown> as Promise<IDemoChatResponse>;
      try {
        const response = await send();
        if (
          !Array.isArray(response.messages) ||
          response.messages.length === 0
        ) {
          console.error('[LINE demo] Agent returned no display messages.', {
            agentSessionUuid: response.agent_session_uuid,
          });
          throw new Error('LINE demo returned no display messages.');
        }
        session = {
          ...session,
          demo_live_session_token: response.demo_live_session_token,
          agent_session_uuid: response.agent_session_uuid,
          // The mint-time snapshot predates this turn; keeping it would rewind
          // the transcript when the chat remounts on a location switch-back.
          history: [],
        };
        cache.set(session);
        if (locationRef.current === requestedLocationId) {
          setActiveSession(session);
          setDataRevision(current => current + 1);
        }
        return response.messages;
      } catch (error) {
        if ((error as { status?: number }).status !== 401) throw error;
        cache.forget(requestedLocationId);
        session = await ensureSession(requestedLocationId);
        const response = await send();
        if (
          !Array.isArray(response.messages) ||
          response.messages.length === 0
        ) {
          console.error(
            '[LINE demo] Agent returned no display messages after session renewal.',
            {
              agentSessionUuid: response.agent_session_uuid,
            }
          );
          throw new Error('LINE demo returned no display messages.');
        }
        session = {
          ...session,
          demo_live_session_token: response.demo_live_session_token,
          agent_session_uuid: response.agent_session_uuid,
          history: [],
        };
        cache.set(session);
        if (locationRef.current === requestedLocationId) {
          setActiveSession(session);
          setDataRevision(current => current + 1);
        }
        return response.messages;
      }
    },
    [cache, ensureSession, language, request]
  );

  const value = useMemo<IDemoSessionContext>(
    () => ({
      accessToken,
      agentSessionUuid,
      lineHistory,
      customerName,
      dataRevision,
      locationId,
      setLocationId,
      ensureSession,
      refreshSession,
      sendLineMessage,
    }),
    [
      accessToken,
      agentSessionUuid,
      customerName,
      dataRevision,
      ensureSession,
      locationId,
      lineHistory,
      refreshSession,
      sendLineMessage,
      setLocationId,
    ]
  );

  return (
    <DemoSessionContext.Provider value={value}>
      {children}
    </DemoSessionContext.Provider>
  );
}

export function useDemoSession(): IDemoSessionContext {
  const context = useContext(DemoSessionContext);
  if (!context) {
    throw new Error('useDemoSession must be used inside DemoSessionProvider');
  }
  return context;
}
